import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getApi } from "../apiCall";

// Dedicated fetchers for the Download Report export. Always request
// group_by=day regardless of whatever grouping is active on screen.
const BASE_URL = `${process.env.REACT_APP_API_URL}/banking/insights/banking_otc/cumulative`;

export const getCumulativeSupplyExport = (discomId, stateId = 6) => {
  const url = `${BASE_URL}/supply?discom_id=${encodeURIComponent(discomId)}&group_by=day&state_id=${encodeURIComponent(stateId)}`;
  return getApi(url);
};

export const getCumulativeReturnExport = (discomId, stateId = 6) => {
  const url = `${BASE_URL}/return?discom_id=${encodeURIComponent(discomId)}&group_by=day&state_id=${encodeURIComponent(stateId)}`;
  return getApi(url);
};

// --- formatting helpers -----------------------------------------------

const dashIfEmpty = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

// Accepts "2026-07-01" (ISO) or "13-12-2026" (DD-MM-YYYY) and returns
// "01-Jul-2026" for both. Returns the original string if unparseable.
const formatPeriod = (value) => {
  if (!value) return "-";

  let date;
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const dmyMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) return value;
  return date
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    .replace(/ /g, "-");
};

const joinOrDash = (arr) =>
  Array.isArray(arr) && arr.length ? arr.join(", ") : "-";

// Returns a finite number, or null (caller renders null as "-" via dashIfEmpty).
const numOrNull = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

// Sizes each column to fit its header + longest cell (capped so no column
// becomes absurdly wide from one long approval-number list).
const autoSizeColumns = (worksheet) => {
  worksheet.columns.forEach((column) => {
    let maxLen = 10;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const len =
        cell.value === null || cell.value === undefined
          ? 0
          : String(cell.value).length;
      if (len > maxLen) maxLen = len;
    });
    column.width = Math.min(maxLen + 2, 40);
  });
};

// --- sheet builders -------------------------------------------------------

const HEADER_FILL_COLOR = "FFED7D31";
const HEADER_ROW_HEIGHT = 24;
const DATA_ROW_HEIGHT = 20;

const styleHeaderRow = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: HEADER_FILL_COLOR },
  };
  headerRow.height = HEADER_ROW_HEIGHT;
  headerRow.eachCell((cell) => {
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });

  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.height = DATA_ROW_HEIGHT;
  });

  const lastColumn = worksheet.columns.length;
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: lastColumn },
  };

  worksheet.views = [{ state: "frozen", ySplit: 1 }];
};

const NUM_FMT = "0.00";

const buildSummarySheet = (workbook, supply, ret) => {
  const sheet = workbook.addWorksheet("Summary");
  sheet.columns = [
    { header: "Metric", key: "metric" },
    { header: "Value", key: "value" },
  ];

  const supplyMetrics = supply?.metrics || {};
  const returnMetrics = ret?.metrics || {};

  const scheduledSupply = numOrNull(supplyMetrics.total_scheduled_mwh);
  const actualSupply = numOrNull(supplyMetrics.total_actual_mwh);
  const supplyVariance =
    scheduledSupply != null && actualSupply != null
      ? actualSupply - scheduledSupply
      : null;

  const returnRequired = numOrNull(
    returnMetrics.return_required_mwh ?? supplyMetrics.return_required_mwh,
  );
  const returnGiven = numOrNull(returnMetrics.total_actual_mwh);
  const returnLeft =
    returnRequired != null && returnGiven != null
      ? returnRequired - returnGiven
      : null;

  const avgReturn = numOrNull(returnMetrics.avg_return_mw);
  const bankingFactor = numOrNull(
    returnMetrics.banking_factor_pct ?? supplyMetrics.banking_factor_pct,
  );

  const windowStart = supplyMetrics.window_start;
  const windowEnd = supplyMetrics.window_end;
  const windowLabel =
    windowStart && windowEnd
      ? `${formatPeriod(windowStart)} - ${formatPeriod(windowEnd)}`
      : "-";

  const rows = [
    ["Scheduled Supply (MWh)", scheduledSupply],
    ["Actual Supply (MWh)", actualSupply],
    ["Supply Variance (MWh)", supplyVariance],
    ["Return Required (MWh)", returnRequired],
    ["Return Given (MWh)", returnGiven],
    ["Return Left (MWh)", returnLeft],
    ["Avg Return (MW)", avgReturn],
    ["Banking Factor (%)", bankingFactor],
    ["Window", windowLabel],
  ];

  rows.forEach(([metric, value]) => {
    const isNumeric = typeof value === "number";
    const row = sheet.addRow({
      metric,
      value: isNumeric ? value : dashIfEmpty(value),
    });
    if (isNumeric) row.getCell("value").numFmt = NUM_FMT;
  });

  styleHeaderRow(sheet);
  autoSizeColumns(sheet);
  return sheet;
};

const buildDailySheet = (workbook, name, daily, includeApprovalCategory) => {
  const columns = [
    { header: "Period", key: "period" },
    { header: "Scheduled (MWh)", key: "scheduled" },
    { header: "Actual (MWh)", key: "actual" },
    { header: "Variance (MWh)", key: "variance" },
    { header: "Variance (%)", key: "variancePct" },
  ];
  if (includeApprovalCategory) {
    columns.push(
      { header: "Approval No", key: "approvalNo" },
      { header: "Category", key: "category" },
    );
  }

  const sheet = workbook.addWorksheet(name);
  sheet.columns = columns;

  const rows = Array.isArray(daily) ? daily : [];
  rows.forEach((row) => {
    const scheduled = numOrNull(row?.scheduled_mwh);
    const actual = numOrNull(row?.actual_mwh);
    const variance = numOrNull(
      row?.delta_mwh ??
        (actual != null && scheduled != null ? actual - scheduled : null),
    );
    const variancePct =
      scheduled != null && scheduled !== 0 && variance != null
        ? (variance / scheduled) * 100
        : null;

    const rowData = {
      period: formatPeriod(row?.date),
      scheduled: scheduled != null ? scheduled : "-",
      actual: actual != null ? actual : "-",
      variance: variance != null ? variance : "-",
      variancePct: variancePct != null ? variancePct : "-",
    };
    if (includeApprovalCategory) {
      rowData.approvalNo = joinOrDash(row?.approval_nos);
      rowData.category = joinOrDash(row?.categories);
    }

    const excelRow = sheet.addRow(rowData);
    if (scheduled != null) excelRow.getCell("scheduled").numFmt = NUM_FMT;
    if (actual != null) excelRow.getCell("actual").numFmt = NUM_FMT;
    if (variance != null) excelRow.getCell("variance").numFmt = NUM_FMT;
    if (variancePct != null) excelRow.getCell("variancePct").numFmt = NUM_FMT;
  });

  styleHeaderRow(sheet);
  autoSizeColumns(sheet);
  return sheet;
};

// "2026-07-20" — YYYY-MM-DD for today, used in the download filename.
const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const generateCumulativeReport = async (discomId, stateId) => {
  let supplyRes;
  let returnRes;
  try {
    [supplyRes, returnRes] = await Promise.all([
      getCumulativeSupplyExport(discomId, stateId),
      getCumulativeReturnExport(discomId, stateId),
    ]);
  } catch (err) {
    throw new Error(
      err?.response?.data?.message || err?.message || "Failed to fetch report data",
    );
  }

  if (supplyRes?.status >= 400 || returnRes?.status >= 400) {
    throw new Error("Failed to fetch report data");
  }

  const supply = supplyRes?.data?.data || supplyRes?.data || {};
  const ret = returnRes?.data?.data || returnRes?.data || {};

  const workbook = new ExcelJS.Workbook();
  buildSummarySheet(workbook, supply, ret);
  buildDailySheet(workbook, "Cumulative Supply", supply?.daily, true);
  buildDailySheet(workbook, "Cumulative Return", ret?.daily, false);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `Banking_Cumulative_Report_${todayIso()}.xlsx`);
};
